import React, { useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';

import FilterComponent from '../DataTableComponents/FilterComponent';
import ColumnVisibilityManager from '../DataTableComponents/ColumnVisibilityManager';
import ExportComponent, { downloadCSV } from '../DataTableComponents/ExportComponent';
import { prepareData } from '../DataTableComponents/DataUtils';

const MachineTableConnected = ({ columns, posts }) => {

  // Tutte le colonne che vogliamo nascondere di default
  const startColumnsHidden = [
    'ID', 'CADID', 'customerID', 'deviceID', 'deviceName', 'deviceSize', 
    'versionNumber', 'created', 'lastMachineCardTimestamp',
    'daysFromPreviousAlarmSent',
    'daysFromPreviousTriggerSent', 'latestTriggerSent',
    'daysFromPreviousMDCSent', 'latestMDCSent'
  ];

  const [hiddenColumns, setHiddenColumns] = useState(startColumnsHidden);

  const toggleColumnVisibility = (columnName) => {
    if (hiddenColumns.includes(columnName)) {
      setHiddenColumns(hiddenColumns.filter((col) => col !== columnName));
    } else {
      setHiddenColumns([...hiddenColumns, columnName]);
    }
  };

  // Preparazione dati
  const { columnManipulation, rawManipulation } = useMemo(
    () => prepareData(posts, columns, hiddenColumns),
    [posts, columns, hiddenColumns]
  );

  // Filtri
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterPLC, setFilterPLC] = useState('');
  const [filterCNC, setFilterCNC] = useState('');
  const [filterTulus, setFilterTulus] = useState('');
  const [filterIOT, setFilterIOT] = useState('');
  const [filterTOB, setFilterTOB] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const filteredItems = rawManipulation.filter(item =>
    item.customer?.toLowerCase().includes(filterCustomer.toLowerCase()) &&
    item.PLC_GCode_version?.toLowerCase().includes(filterPLC.toLowerCase()) &&
    item.CNC_version?.toLowerCase().includes(filterCNC.toLowerCase()) &&
    item.Tulus_version?.toLowerCase().includes(filterTulus.toLowerCase()) &&
    item.IoT_version?.toLowerCase().includes(filterIOT.toLowerCase()) &&
    item.TOB_version?.toLowerCase().includes(filterTOB.toLowerCase())
  );

  // Filtri memo
  const subHeaderComponentMemo = useMemo(() => {
    const PLC_GCode_version = [...new Set(rawManipulation.map(i => i.PLC_GCode_version).filter(Boolean))];
    const CNC_version = [...new Set(rawManipulation.map(i => i.CNC_version).filter(Boolean))];
    const Tulus_version = [...new Set(rawManipulation.map(i => i.Tulus_version).filter(Boolean))];
    const IoT_version = [...new Set(rawManipulation.map(i => i.IoT_version).filter(Boolean))];
    const TOB_version = [...new Set(rawManipulation.map(i => i.TOB_version).filter(Boolean))];

    const handleClear = {
      customer: () => { setFilterCustomer(''); setResetPaginationToggle(!resetPaginationToggle); },
      PLC: () => { setFilterPLC(''); setResetPaginationToggle(!resetPaginationToggle); },
      CNC: () => { setFilterCNC(''); setResetPaginationToggle(!resetPaginationToggle); },
      Tulus: () => { setFilterTulus(''); setResetPaginationToggle(!resetPaginationToggle); },
      IoT: () => { setFilterIOT(''); setResetPaginationToggle(!resetPaginationToggle); },
      TOB: () => { setFilterTOB(''); setResetPaginationToggle(!resetPaginationToggle); },
    };

    return (
      <FilterComponent
        filterCustomer={filterCustomer} onFilterCustomer={e => setFilterCustomer(e.target.value)}
        filterPLC={filterPLC} onFilterPLC={e => setFilterPLC(e.target.value)}
        filterCNC={filterCNC} onFilterCNC={e => setFilterCNC(e.target.value)}
        filterTulus={filterTulus} onFilterTulus={e => setFilterTulus(e.target.value)}
        filterIOT={filterIOT} onFilterIOT={e => setFilterIOT(e.target.value)}
        filterTOB={filterTOB} onFilterTOB={e => setFilterTOB(e.target.value)}
        optionsPLC={PLC_GCode_version} optionsCNC={CNC_version} optionsTulus={Tulus_version} optionsIOT={IoT_version} optionsTOB={TOB_version}
        onClearCustomer={handleClear.customer} onClearPLC={handleClear.PLC} onClearCNC={handleClear.CNC} onClearTulus={handleClear.Tulus} onClearIOT={handleClear.IoT} onClearTOB={handleClear.TOB}
      />
    );
  }, [filterCustomer, filterPLC, filterCNC, filterTulus, filterIOT, filterTOB, rawManipulation, resetPaginationToggle]);

  // Conditional row style basato solo su visionType
  const conditionalRowStyles = [
    {
      when: row => row["daysFromPreviousAlarmSent"] > 30,
      style: { backgroundColor: 'rgba(255,165,0,0.9)', color: 'black' }
    },
    {
      when: row => row["daysFromPreviousAlarmSent"] < 0,
      style: { backgroundColor: 'rgba(150,40,27,0.9)', color: 'white' }
    }
  ];

  const customStyles = {
    headCells: { style: { fontWeight: 'bold', fontSize: '12px', padding: '8px' } },
    cells: { style: { fontSize: '11px', padding: '4px 8px' } },
    rows: { style: { minHeight: '32px' } },
  };

  return (
    <>
      <ColumnVisibilityManager
        columns={columnManipulation}
        hiddenColumns={hiddenColumns}
        toggleColumnVisibility={toggleColumnVisibility}
      />

      <DataTable
        columns={columnManipulation.filter(c => !c.omit)}
        data={filteredItems}
        pagination
        paginationPerPage={15}
        actions={<ExportComponent onExport={() => downloadCSV(rawManipulation)} />}
        paginationResetDefaultPage={resetPaginationToggle}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        persistTableHead
        conditionalRowStyles={conditionalRowStyles}
        className='disable-sticky'
        customStyles={customStyles}
      />
    </>
  );
};

export default MachineTableConnected;
